import React, { useState, useEffect } from 'react';
import { Network, TrendingUp, GitBranch, Target } from 'lucide-react';
import { adminAPI } from '../services/api';

function KnowledgeGraph() {
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);

  useEffect(() => {
    loadGraph();
  }, []);

  const loadGraph = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getKnowledgeGraph();
      setGraphData(response.data);
    } catch (error) {
      console.error('Error loading graph:', error);
      // Set sample graph data
      setGraphData({
        nodes: [
          { id: 'campaign_1', type: 'campaign' },
          { id: 'channel_search', type: 'channel' },
          { id: 'audience_enterprise', type: 'audience' },
          { id: 'creative_video', type: 'creative' }
        ],
        edges: [
          { source: 'campaign_1', target: 'channel_search', relationship: 'used_channel' },
          { source: 'campaign_1', target: 'audience_enterprise', relationship: 'targeted' },
          { source: 'campaign_1', target: 'creative_video', relationship: 'featured' }
        ],
        stats: { total_nodes: 4, total_edges: 3 }
      });
    } finally {
      setLoading(false);
    }
  };

  const getNodeColor = (type) => {
    const colors = {
      campaign: 'var(--primary-500)',
      channel: 'var(--accent-500)',
      audience: 'var(--success-500)',
      creative: 'var(--warning-500)'
    };
    return colors[type] || 'var(--secondary-500)';
  };

  if (loading) {
    return (
      <div className="card">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Marketing Knowledge Graph</h3>
        <p className="card-subtitle">Discover relationships and unexplored opportunities</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3" style={{ gap: '12px', marginBottom: '24px' }}>
        <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px', textAlign: 'center' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>Total Nodes</p>
          <p style={{ fontSize: '2rem', fontWeight: '700' }}>{graphData?.stats?.total_nodes || 0}</p>
        </div>
        <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px', textAlign: 'center' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>Total Edges</p>
          <p style={{ fontSize: '2rem', fontWeight: '700' }}>{graphData?.stats?.total_edges || 0}</p>
        </div>
        <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px', textAlign: 'center' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '4px' }}>Relationships</p>
          <p style={{ fontSize: '2rem', fontWeight: '700' }}>{graphData?.edges?.length || 0}</p>
        </div>
      </div>

      {/* Graph Visualization (Simplified) */}
      <div style={{ padding: '24px', background: 'var(--bg-tertiary)', borderRadius: '12px', minHeight: '300px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center' }}>
          {graphData?.nodes?.map((node, idx) => (
            <div
              key={idx}
              onClick={() => setSelectedNode(node)}
              style={{
                padding: '16px 24px',
                background: getNodeColor(node.type),
                color: 'white',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                border: selectedNode?.id === node.id ? '3px solid white' : 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
              data-testid={`graph-node-${node.id}`}
            >
              {node.id.replace('_', ' ')}
            </div>
          ))}
        </div>

        {selectedNode && (
          <div style={{ marginTop: '24px', padding: '16px', background: 'white', borderRadius: '8px' }}>
            <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '8px' }}>Node Details</h4>
            <p><strong>ID:</strong> {selectedNode.id}</p>
            <p><strong>Type:</strong> {selectedNode.type}</p>
            
            {/* Connected nodes */}
            <div style={{ marginTop: '12px' }}>
              <p style={{ fontWeight: '600', marginBottom: '8px' }}>Connected To:</p>
              {graphData.edges
                .filter(e => e.source === selectedNode.id || e.target === selectedNode.id)
                .map((edge, idx) => (
                  <p key={idx} style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    {edge.relationship}: {edge.source === selectedNode.id ? edge.target : edge.source}
                  </p>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Unexplored Combinations */}
      <div style={{ marginTop: '24px' }}>
        <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <GitBranch size={20} />
          Unexplored Opportunities
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ padding: '12px', background: 'hsl(142, 76%, 96%)', border: '1px solid hsl(142, 56%, 85%)', borderRadius: '8px' }}>
            <p style={{ fontSize: '0.875rem', fontWeight: '600' }}>💡 Video creative has never been paired with Enterprise audience</p>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>Potential for new campaign targeting</p>
          </div>
          <div style={{ padding: '12px', background: 'hsl(142, 76%, 96%)', border: '1px solid hsl(142, 56%, 85%)', borderRadius: '8px' }}>
            <p style={{ fontSize: '0.875rem', fontWeight: '600' }}>💡 Display channel unexplored for Product Launch objectives</p>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>Consider testing this combination</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default KnowledgeGraph;
